import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { isEmpty } from 'lodash-es';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { NO_VALUE } from '@pipes';
import {
  COUNTRIES_ERROR_CODE_SEARCH,
  ErrorDetailsStore,
  LANGUAGES,
  SearchStore,
  SearchType,
} from '@store';

import { SupportedCountry } from '../../../../shared/models/supportedCountry.model';
import { SupportedCountriesService } from '../../../../shared/services/supported-countries.service';
import { SearchService } from '../../../search.service';
import { ErrorSearchFormGroup } from './error-search.helper';

@Component({
  selector: 'app-error-search',
  templateUrl: './error-search.component.html',
  styleUrls: ['./error-search.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ErrorSearchComponent implements OnInit, OnDestroy {
  private ngUnsubscribe$: Subject<void> = new Subject<void>();
  private supportedCountries: SupportedCountry[];

  errorCode = '';
  selectedCountry = 'DE';
  countries: string[];
  formGroup: FormGroup<ErrorSearchFormGroup>;

  constructor(
    public searchService: SearchService,
    private searchStore: SearchStore,
    private cdr: ChangeDetectorRef,
    private errorDetailsStore: ErrorDetailsStore,
    private translateService: TranslateService,
    private supportedCountriesService: SupportedCountriesService
  ) {}

  ngOnInit(): void {
    this.supportedCountries = this.supportedCountriesService.getAllSupportedCountries();
    this.countries = this.sortAlphabetically(COUNTRIES_ERROR_CODE_SEARCH);
    this.formGroup = new FormGroup<ErrorSearchFormGroup>({
      materialNumber: new FormControl<string>('', [Validators.required, Validators.minLength(7)]),
    });

    this.searchService.errorSearchParams$
      .pipe(
        takeUntil(this.ngUnsubscribe$),
        filter((data) => !isEmpty(data))
      )
      .subscribe((data) => {
        this.formGroup.setValue({ materialNumber: data.searchParams.materialNumber });
        this.errorCode = data.searchParams.errorCode;
        this.selectedCountry = this.searchService.transformCountryCodeToUpperCase(
          data.searchParams.countryCode
        );

        this.cdr.detectChanges();

        window.scrollTo(0, 80);

        this.search();
      });
  }

  search(): void {
    this.errorDetailsStore.clearErrorState();

    if (!this.formGroup.invalid && this.errorCode) {
      this.searchService.search$.next(SearchType.error);

      const country: SupportedCountry = this.supportedCountries.find(
        (c: SupportedCountry) => c.countryCode.toLowerCase() === this.selectedCountry.toLowerCase()
      );
      this.searchStore.searchErrors(
        this.formGroup.value.materialNumber,
        this.errorCode,
        this.searchService.transformCountryCodeToUpperCase(country.countryCode),
        country.languageCode
      );
    }
  }

  getLanguage(country: string): string {
    const languageCode = country.split('_')[1];
    if (languageCode) {
      const language = LANGUAGES.find((lang) => lang.code === languageCode);

      return language && language.name ? language.name : undefined;
    } else {
      return undefined;
    }
  }

  clear(): void {
    this.errorCode = '';
    this.selectedCountry = 'DE';
    this.formGroup.get('materialNumber').setValue('');
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
    this.searchService.errorSearchParams$.next(undefined);
  }

  private translate(countryCode: string): string {
    countryCode = countryCode.toLowerCase();
    const country: SupportedCountry = this.supportedCountries.find(
      (c: SupportedCountry) => c.countryCode.toLowerCase() === countryCode
    );
    if (country) {
      return this.translateService.instant(country.country);
    } else if (countryCode) {
      return countryCode;
    } else {
      return NO_VALUE;
    }
  }

  private sortAlphabetically(array: string[]): string[] {
    return array.sort((a, b) => {
      const aTranslated = `${this.translate(a)} ${this.getLanguage(a) ?? ''}`;
      const bTranslated = `${this.translate(b)} ${this.getLanguage(b) ?? ''}`;

      return aTranslated > bTranslated ? 1 : -1;
    });
  }
}
